import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Trash2, Plus, Search, Download, Check, ChevronsUpDown, Edit2, Save, X, History } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format part number
const formatPartNumber = (partNumber) => {
  const cleaned = partNumber.trim();
  if (cleaned.length === 4 && /^\d{4}$/.test(cleaned)) {
    return '00' + cleaned;
  }
  return cleaned;
};

function TurnedParts() {
  const [stockInRecords, setStockInRecords] = useState([]);
  const [stockOutRecords, setStockOutRecords] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchPartNumber, setSearchPartNumber] = useState('');
  const [stockInfo, setStockInfo] = useState(null);
  const [allStock, setAllStock] = useState([]);
  const [projectSelectOpen, setProjectSelectOpen] = useState(false);
  const [isEditingStock, setIsEditingStock] = useState(false);
  const [editStockForm, setEditStockForm] = useState({ quantity: '', unit_price: '' });
  const [savingStock, setSavingStock] = useState(false);
  const [downloadingStockIn, setDownloadingStockIn] = useState(false);
  
  const [stockInForm, setStockInForm] = useState({
    supplier_id: '',
    part_number: '',
    quantity: '',
    unit_price: ''
  });

  const [stockOutForm, setStockOutForm] = useState({
    date: new Date().toISOString().split('T')[0],
    part_number: '',
    quantity: '',
    project_number: ''
  });

  useEffect(() => {
    fetchStockInRecords();
    fetchStockOutRecords();
    fetchSuppliers();
    fetchAllStock();
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers', error);
    }
  };

  const fetchStockInRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/turned-parts/stock-in`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStockInRecords(response.data);
    } catch (error) {
      console.error('Failed to fetch stock-in records', error);
    }
  };

  const fetchStockOutRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/turned-parts/stock-out`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStockOutRecords(response.data);
    } catch (error) {
      console.error('Failed to fetch stock-out records', error);
    }
  };

  const fetchAllStock = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/turned-parts/stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllStock(response.data);
    } catch (error) {
      console.error('Failed to fetch stock', error);
    }
  };

  const handleStockInSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        supplier_id: stockInForm.supplier_id,
        part_number: stockInForm.part_number,
        quantity: parseFloat(stockInForm.quantity),
        unit_price: parseFloat(stockInForm.unit_price) || 0
      };

      await axios.post(`${BACKEND_URL}/api/turned-parts/stock-in`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Stock-In record created successfully');
      setStockInForm({
        supplier_id: '',
        part_number: '',
        quantity: '',
        unit_price: ''
      });
      fetchStockInRecords();
      fetchAllStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create record');
    }
  };

  const handleStockOutSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        date: new Date(stockOutForm.date).toISOString(),
        part_number: stockOutForm.part_number,
        quantity: parseFloat(stockOutForm.quantity),
        project_number: stockOutForm.project_number || ''
      };

      await axios.post(`${BACKEND_URL}/api/turned-parts/stock-out`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Stock-Out record created successfully');
      setStockOutForm({
        date: new Date().toISOString().split('T')[0],
        part_number: '',
        quantity: '',
        project_number: ''
      });
      fetchStockOutRecords();
      fetchAllStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create record');
    }
  };

  const handleDownloadStockInExcel = async () => {
    setDownloadingStockIn(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/turned-parts/stock-in-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `turned_parts_stock_in_${new Date().toISOString().split('T')[0]}.xlsx`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Stock-In Excel downloaded (last 3 months)');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingStockIn(false);
    }
  };

  const handleSearchStock = async (e) => {
    e.preventDefault();
    if (!searchPartNumber) {
      toast.error('Please enter a part number');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/turned-parts/stock`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { part_number: searchPartNumber }
      });
      setStockInfo(response.data);
      setIsEditingStock(false);
      setEditStockForm({ 
        quantity: response.data.available_quantity.toString(), 
        unit_price: (response.data.unit_price || 0).toString() 
      });
    } catch (error) {
      toast.error('Failed to fetch stock information');
    }
  };

  const handleEditStockToggle = () => {
    if (stockInfo) {
      setIsEditingStock(true);
      setEditStockForm({ 
        quantity: stockInfo.available_quantity.toString(), 
        unit_price: (stockInfo.unit_price || 0).toString() 
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingStock(false);
    setEditStockForm({ 
      quantity: stockInfo?.available_quantity.toString() || '', 
      unit_price: (stockInfo?.unit_price || 0).toString() 
    });
  };

  const handleSaveStock = async () => {
    if (!stockInfo) return;

    setSavingStock(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/turned-parts/stock/${stockInfo.part_number}`,
        {
          quantity: parseFloat(editStockForm.quantity) || 0,
          unit_price: parseFloat(editStockForm.unit_price) || 0
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Stock updated successfully');
      setIsEditingStock(false);
      
      // Refresh the stock info
      const response = await axios.get(`${BACKEND_URL}/api/turned-parts/stock`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { part_number: stockInfo.part_number }
      });
      setStockInfo(response.data);
      setEditStockForm({ 
        quantity: response.data.available_quantity.toString(), 
        unit_price: (response.data.unit_price || 0).toString() 
      });
      
      // Refresh all stock data
      fetchAllStock();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update stock');
    } finally {
      setSavingStock(false);
    }
  };

  const handleProjectSelect = (westawProjectNumber) => {
    setStockOutForm({ ...stockOutForm, project_number: westawProjectNumber });
    setProjectSelectOpen(false);
  };

  const getSelectedProjectDisplay = () => {
    const selectedProject = projects.find(p => p.westaw_project_number === stockOutForm.project_number);
    return selectedProject ? `${selectedProject.westaw_project_number} - ${selectedProject.client_name}` : 'Search and select a project...';
  };

  const handleDeleteStockIn = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/turned-parts/stock-in/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Record deleted successfully');
      fetchStockInRecords();
      fetchAllStock();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  const handleDeleteStockOut = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/turned-parts/stock-out/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Record deleted successfully');
      fetchStockOutRecords();
      fetchAllStock();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  const handlePartNumberChange = (value, formType) => {
    const formatted = formatPartNumber(value);
    if (formType === 'stock-in') {
      setStockInForm({ ...stockInForm, part_number: formatted });
    } else if (formType === 'stock-out') {
      setStockOutForm({ ...stockOutForm, part_number: formatted });
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/turned-parts/stock-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'turned_parts_inventory.xlsx');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Excel file');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="turned-parts-title">
          Turned Parts
        </h1>
        <p className="text-slate-600 mt-2">Manage turned parts inventory</p>
      </div>

      <Tabs defaultValue="stock-in" className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-3">
          <TabsTrigger value="stock-in" data-testid="stock-in-tab">Stock-In</TabsTrigger>
          <TabsTrigger value="stock-out" data-testid="stock-out-tab">Stock-Out</TabsTrigger>
          <TabsTrigger value="stock" data-testid="stock-tab">Stock</TabsTrigger>
        </TabsList>

        {/* Stock-In Tab */}
        <TabsContent value="stock-in">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-semibold">Add Stock-In</CardTitle>
                  <Button
                    onClick={handleDownloadStockInExcel}
                    disabled={downloadingStockIn}
                    variant="outline"
                    size="sm"
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    data-testid="download-stock-in-excel"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloadingStockIn ? 'Downloading...' : 'Download Excel (3 Months)'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleStockInSubmit} className="space-y-4" data-testid="stock-in-form">
                  <div className="space-y-2">
                    <Label htmlFor="supplier">Supplier</Label>
                    <Select value={stockInForm.supplier_id} onValueChange={(value) => setStockInForm({ ...stockInForm, supplier_id: value })} required>
                      <SelectTrigger data-testid="stock-in-supplier-select">
                        <SelectValue placeholder="Select a supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="part_number">Part Number (6 digits or 4 digits)</Label>
                    <Input
                      id="part_number"
                      data-testid="stock-in-part-number-input"
                      value={stockInForm.part_number}
                      onChange={(e) => setStockInForm({ ...stockInForm, part_number: e.target.value })}
                      onBlur={(e) => handlePartNumberChange(e.target.value, 'stock-in')}
                      placeholder="e.g., 1234 or 001234"
                      required
                    />
                    <p className="text-xs text-slate-500">Enter 4 digits and it will auto-format to 00XXXX</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quantity_in">Quantity</Label>
                    <Input
                      id="quantity_in"
                      data-testid="stock-in-quantity-input"
                      type="number"
                      step="0.01"
                      value={stockInForm.quantity}
                      onChange={(e) => setStockInForm({ ...stockInForm, quantity: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="unit_price_in">Unit Price (₹)</Label>
                    <Input
                      id="unit_price_in"
                      data-testid="stock-in-unit-price-input"
                      type="number"
                      step="0.01"
                      value={stockInForm.unit_price}
                      onChange={(e) => setStockInForm({ ...stockInForm, unit_price: e.target.value })}
                      placeholder="Enter unit price"
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-stock-in-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stock-In
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Recent Stock-In</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {stockInRecords.slice(0, 10).map((record) => (
                    <div key={record.id} className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-slate-900">Part: {record.part_number}</p>
                          <p className="text-sm text-slate-600">Supplier: {record.supplier_name}</p>
                          {record.unit_price > 0 && (
                            <p className="text-sm text-emerald-600 font-medium">Price: ₹{record.unit_price?.toFixed(2)}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteStockIn(record.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex justify-between text-sm text-slate-600">
                        <span className="font-semibold text-emerald-700">Quantity: {record.quantity}</span>
                        <span>{new Date(record.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                  {stockInRecords.length === 0 && (
                    <div className="text-center py-8 text-slate-600">No stock-in records yet</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Stock-Out Tab */}
        <TabsContent value="stock-out">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Add Stock-Out</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleStockOutSubmit} className="space-y-4" data-testid="stock-out-form">
                  <div className="space-y-2">
                    <Label htmlFor="date_out">Date</Label>
                    <Input
                      id="date_out"
                      data-testid="stock-out-date-input"
                      type="date"
                      value={stockOutForm.date}
                      onChange={(e) => setStockOutForm({ ...stockOutForm, date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="part_number_out">Part Number (6 digits or 4 digits)</Label>
                    <Input
                      id="part_number_out"
                      data-testid="stock-out-part-number-input"
                      value={stockOutForm.part_number}
                      onChange={(e) => setStockOutForm({ ...stockOutForm, part_number: e.target.value })}
                      onBlur={(e) => handlePartNumberChange(e.target.value, 'stock-out')}
                      placeholder="e.g., 1234 or 001234"
                      required
                    />
                    <p className="text-xs text-slate-500">Enter 4 digits and it will auto-format to 00XXXX</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quantity_out">Quantity</Label>
                    <Input
                      id="quantity_out"
                      data-testid="stock-out-quantity-input"
                      type="number"
                      step="0.01"
                      value={stockOutForm.quantity}
                      onChange={(e) => setStockOutForm({ ...stockOutForm, quantity: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="project_number_out">WESTAW Project</Label>
                    <Popover open={projectSelectOpen} onOpenChange={setProjectSelectOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={projectSelectOpen}
                          className="w-full justify-between font-normal"
                          data-testid="stock-out-project-select"
                        >
                          <span className={stockOutForm.project_number ? "text-slate-900" : "text-slate-500"}>
                            {getSelectedProjectDisplay()}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search projects..." />
                          <CommandList>
                            <CommandEmpty>No project found.</CommandEmpty>
                            <CommandGroup>
                              {projects.map((project) => (
                                <CommandItem
                                  key={project.id}
                                  value={`${project.westaw_project_number} ${project.client_name} ${project.client_project_number}`}
                                  onSelect={() => handleProjectSelect(project.westaw_project_number)}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      stockOutForm.project_number === project.westaw_project_number ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{project.westaw_project_number}</span>
                                    <span className="text-xs text-slate-500">{project.client_name} • {project.client_project_number}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-stock-out-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stock-Out
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Recent Stock-Out</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {stockOutRecords.slice(0, 10).map((record) => (
                    <div key={record.id} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-slate-900">Part: {record.part_number}</p>
                          <p className="text-sm text-slate-600">Date: {new Date(record.date).toLocaleDateString()}</p>
                          {record.project_number && (
                            <p className="text-sm text-sky-600 font-medium">Project: {record.project_number}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteStockOut(record.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="font-semibold text-red-700">Quantity: {record.quantity}</span>
                      </div>
                    </div>
                  ))}
                  {stockOutRecords.length === 0 && (
                    <div className="text-center py-8 text-slate-600">No stock-out records yet</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Stock Tab */}
        <TabsContent value="stock">
          <div className="space-y-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-xl font-semibold">Search Part Stock</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSearchStock} className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Enter part number (4 or 6 digits)"
                      value={searchPartNumber}
                      onChange={(e) => setSearchPartNumber(e.target.value)}
                      onBlur={(e) => setSearchPartNumber(formatPartNumber(e.target.value))}
                      data-testid="search-part-number-input"
                    />
                  </div>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="search-stock-button">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </form>

                {stockInfo && (
                  <div className="mt-6 space-y-6">
                    {/* Stock Info Card */}
                    <div className="p-6 bg-sky-50 border-2 border-sky-200 rounded-lg" data-testid="stock-info-result">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-2xl font-bold text-slate-900">Part Number: {stockInfo.part_number}</h3>
                        {!isEditingStock ? (
                          <Button 
                            onClick={handleEditStockToggle} 
                            variant="outline" 
                            size="sm"
                            className="border-sky-300 text-sky-700 hover:bg-sky-100"
                            data-testid="edit-stock-button"
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit Stock
                          </Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button 
                              onClick={handleSaveStock} 
                              size="sm"
                              disabled={savingStock}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid="save-stock-button"
                            >
                              <Save className="w-4 h-4 mr-2" />
                              {savingStock ? 'Saving...' : 'Save'}
                            </Button>
                            <Button 
                              onClick={handleCancelEdit} 
                              variant="outline" 
                              size="sm"
                              data-testid="cancel-edit-button"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {!stockInfo.exists && stockInfo.available_quantity === 0 && (
                        <div className="mb-4 p-3 bg-amber-100 border border-amber-300 rounded-lg text-amber-800 text-sm">
                          This part number does not exist in the system yet. You can create stock entries from the Stock-In tab.
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-slate-200">
                          <p className="text-sm text-slate-600">Total Stock-In</p>
                          <p className="text-3xl font-bold text-emerald-700">{stockInfo.total_in}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-200">
                          <p className="text-sm text-slate-600">Total Stock-Out</p>
                          <p className="text-3xl font-bold text-red-700">{stockInfo.total_out}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-sky-300">
                          <p className="text-sm text-slate-600">Available Quantity</p>
                          {isEditingStock ? (
                            <Input
                              type="number"
                              value={editStockForm.quantity}
                              onChange={(e) => setEditStockForm({...editStockForm, quantity: e.target.value})}
                              className="mt-1 text-xl font-bold"
                              data-testid="edit-quantity-input"
                            />
                          ) : (
                            <p className="text-3xl font-bold text-sky-700">{stockInfo.available_quantity}</p>
                          )}
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-amber-300">
                          <p className="text-sm text-slate-600">Unit Price (₹)</p>
                          {isEditingStock ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={editStockForm.unit_price}
                              onChange={(e) => setEditStockForm({...editStockForm, unit_price: e.target.value})}
                              className="mt-1 text-xl font-bold"
                              data-testid="edit-price-input"
                            />
                          ) : (
                            <p className="text-3xl font-bold text-amber-700">₹{stockInfo.unit_price?.toFixed(2) || '0.00'}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Stock-Out History - Last 6 Months */}
                    {stockInfo.stock_out_history && stockInfo.stock_out_history.length > 0 && (
                      <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg" data-testid="stock-out-history">
                        <div className="flex items-center gap-2 mb-4">
                          <History className="w-5 h-5 text-slate-600" />
                          <h4 className="text-lg font-semibold text-slate-900">Stock-Out History (Last 6 Months)</h4>
                        </div>
                        <div className="rounded-lg border border-slate-200 overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Date</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Project Number</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Quantity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stockInfo.stock_out_history.map((record, index) => (
                                <tr key={index} className="border-t border-slate-200 hover:bg-white">
                                  <td className="px-4 py-3 text-sm text-slate-900">{record.date}</td>
                                  <td className="px-4 py-3 text-sm">
                                    {record.project_number ? (
                                      <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
                                        {record.project_number}
                                      </Badge>
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm font-semibold text-red-700">{record.quantity}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {stockInfo.stock_out_history && stockInfo.stock_out_history.length === 0 && stockInfo.exists && (
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 text-sm">
                        No stock-out history in the last 6 months for this part.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
                <CardTitle className="text-xl font-semibold">All Parts Stock</CardTitle>
                <Button 
                  onClick={handleDownloadExcel} 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="download-stock-excel"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Excel
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full" data-testid="all-stock-table">
                    <thead className="bg-slate-50">
                      <tr className="border-b border-slate-200">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Part Number</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Total Stock-In</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Total Stock-Out</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Available Qty</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Unit Price</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allStock.map((item) => (
                        <tr key={item.part_number} className="hover:bg-slate-50/50 border-b border-slate-100 last:border-0">
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.part_number}</td>
                          <td className="px-6 py-4 text-sm text-emerald-700 font-semibold">{item.total_in}</td>
                          <td className="px-6 py-4 text-sm text-red-700 font-semibold">{item.total_out}</td>
                          <td className="px-6 py-4 text-sm text-sky-700 font-bold">{item.available_quantity}</td>
                          <td className="px-6 py-4 text-sm text-amber-700 font-semibold">₹{item.unit_price?.toFixed(2) || '0.00'}</td>
                          <td className="px-6 py-4 text-sm">
                            <Badge className={item.available_quantity > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}>
                              {item.available_quantity > 0 ? 'In Stock' : 'Out of Stock'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {allStock.length === 0 && (
                    <div className="p-8 text-center text-slate-600">No stock data available</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TurnedParts;
